import React from 'react';
import { ptBR } from 'date-fns/locale';
import DateFnsUtils from '@date-io/date-fns';
import { makeStyles } from '@material-ui/styles';
import { Column, Action, Options } from 'material-table';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import { MuiPickersUtilsProvider, DatePicker } from '@material-ui/pickers';
import { Button, createStyles, Grid, Icon, IconButton, InputAdornment, TextField, Typography } from '@material-ui/core';

import { theme } from '../../../../theme';
import {  ClientContext } from '../../ClientContext';
import { EditAction } from '../../../../shared/enum/EditAction';
import { AuthContext } from '../../../Authentication/AuthContext';
import { TableEmpty } from '../../../../shared/components/TableEmpty';
import { TableCommon } from '../../../../shared/components/TableCommon';
import { getTableColumn, numberFormat } from '../../../../shared/operators';
import { BasicConfirmDialog } from '../../../../shared/components/BasicConfirmDialog';
import { SocialMediaRateModel, socialMediaRateModelInitial } from '../../../../Models/SocialMediaRateModel';
import { HomeContext } from '../../../Home/HomeContext';

export interface IClientSocialMediaItemProps {
    itemTitle: string,
    contextRateList: SocialMediaRateModel[],
    setContextRateList: (rateList: SocialMediaRateModel[]) => void
}

const useStyles = makeStyles(() => createStyles({
    textButton: {
        paddingLeft: 0,
        '&:hover': {
            textDecorationLine: 'underline',
        },
    },
}));

export function ClientSocialMediaItem({ itemTitle, contextRateList, setContextRateList }: IClientSocialMediaItemProps) {
    const classes = useStyles();

    const authContext = React.useContext(AuthContext);
    const homeContext = React.useContext(HomeContext);
    const clientContext = React.useContext(ClientContext);

    const [modalRateValue, setModalRateValue] = React.useState<number | null>(null);
    const [modalRateStartOfPeriod, setModalRateStartOfPeriod] = React.useState<Date | null>(null);
    const [deleteIndex, setDeleteIndex] = React.useState<number | null>(null);
    const [addNewRateDialogOpen, setAddNewRateDialogOpen] = React.useState<boolean>(false);
    const [deleteRateDialogOpen, setDeleteRateDialogOpen] = React.useState<boolean>(false);

    const [modalRateValueError, setModalRateValueError] = React.useState<boolean>(false);
    const [modalRateStartOfPeriodError, setModalRateStartOfPeriodError] = React.useState<boolean>(false);
    const [modalRateStartOfPeriodFocused, setModalRateStartOfPeriodFocused] = React.useState<boolean>(false);

    const rateTableColumns: Column<SocialMediaRateModel>[] = [
        getTableColumn('Valor', 'rateValue', 'string', 'left'),
        getTableColumn('Data de início de vigência', 'startOfPeriod', 'date', 'left', { maxWidth: '150px' }, 'desc'),
        getTableColumn('Data fim de vigência', 'endOfPeriod', 'date', 'left'),
    ];
    
    const rateTableOptions: Options<SocialMediaRateModel> = {
        filtering: true,
        sorting: true,
        showTitle: false,
        toolbar: false,
        headerStyle: {
            fontSize: '14px',
            whiteSpace: 'nowrap'
        },
        pageSizeOptions: [5, 10, 15],
        pageSize: 5,
    };
    
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const rateTableActions: (((rowData: any) => Action<any>))[] = [
        (rowData: SocialMediaRateModel) => ({
            icon: 'delete_outline',
            iconProps: { style: { fontSize: '20px' } },
            tooltip: 'Excluir taxa',
            onClick: (_, rowData) => { setDeleteIndex(rowData.tableData.id); setDeleteRateDialogOpen(true); },
            disabled: rowData.endOfPeriod != null,
            hidden: rowData.endOfPeriod != null,
        }),
    ];

    const rateModalHasError = () => {
        const rateValueError = !modalRateValue || isNaN(modalRateValue) || modalRateValue < 0;
        const startOfPeriodError = !modalRateStartOfPeriod;

        setModalRateValueError(rateValueError);
        setModalRateStartOfPeriodError(startOfPeriodError);

        return rateValueError || startOfPeriodError;
    };

    const handleModalRateValueChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = numberFormat(event.target.value);
        setModalRateValue(value);
    };

    const handleDeleteCurrentRate = () => {
        if (deleteIndex != null && contextRateList && contextRateList.length > 0) {
            const rateList: SocialMediaRateModel[] = [...contextRateList];

            if (deleteIndex > 0) {
                let previous = 1;
                
                while (previous != 0) {
                    if (rateList[deleteIndex - previous].editAction == EditAction.DELETE)
                        previous += 1;
                    else {
                        if (rateList[deleteIndex - previous].editAction != EditAction.INSERT)
                            rateList[deleteIndex - previous].editAction = EditAction.UPDATE;
                            
                        rateList[deleteIndex - previous].endOfPeriod = null;
                        previous = 0;
                    }
                }
            }

            if (rateList[deleteIndex].editAction === EditAction.INSERT)
                rateList.splice(deleteIndex, 1);
            else if (rateList[deleteIndex].editAction === EditAction.NONE 
                  || rateList[deleteIndex].editAction === EditAction.UPDATE) {

                rateList[deleteIndex].editAction = EditAction.DELETE;
            }

            setDeleteRateDialogOpen(false);
            setContextRateList(rateList);
        }
    };

    const handleCloseAndClearModal = () => {
        setAddNewRateDialogOpen(false);
        setModalRateValue(null);
        setModalRateStartOfPeriod(null);
        setModalRateValueError(false);
        setModalRateStartOfPeriodError(false);
        setDeleteIndex(null);
    };

    const handleRateModalSave = () => {
        if (!rateModalHasError()){
            const newRate: SocialMediaRateModel = {...socialMediaRateModelInitial};
            newRate.clientId = clientContext.client.id;
            newRate.editAction = EditAction.INSERT;
            newRate.rateValue = modalRateValue ?? 0;
            newRate.userId = authContext.login.user.id;

            if (modalRateStartOfPeriod != null)
                newRate.startOfPeriod = new Date(modalRateStartOfPeriod.getFullYear(),
                    modalRateStartOfPeriod.getMonth(),
                    modalRateStartOfPeriod.getDate(),
                    0, 0, 0);
                
            const rateList: SocialMediaRateModel[] = contextRateList && contextRateList.length > 0 
                ? [...contextRateList]
                : [];

            if (newRate.startOfPeriod && rateList.filter(r => r.editAction != EditAction.DELETE).length > 0) {
                const activeRateIndex = rateList.findIndex(r => r.endOfPeriod == null && r.editAction != EditAction.DELETE);
                const activeRate = rateList[activeRateIndex];

                if (activeRate.startOfPeriod && (activeRate.startOfPeriod >= newRate.startOfPeriod)) {
                    homeContext.showWarning('Já possui uma taxa de conversão com vigência neste período, é preciso excluí-la antes de adicionar uma nova.');

                    return;
                } else {
                    const inactiveDate = new Date(newRate.startOfPeriod);
                    inactiveDate.setSeconds(inactiveDate.getSeconds() - 1);

                    rateList[activeRateIndex].endOfPeriod = inactiveDate;

                    if (activeRate.editAction != EditAction.INSERT)
                        rateList[activeRateIndex].editAction = EditAction.UPDATE;
                }
            }

            rateList.push(newRate);
            setContextRateList(rateList);

            handleCloseAndClearModal();
        }
    };

    const getAddNewButton = () => {
        return (
            <Grid item xs={12}>
                <Button
                    onClick={() => setAddNewRateDialogOpen(true)}
                    className={classes.textButton}
                    startIcon={<Icon color='primary'>add</Icon>}>
                    Adicionar Taxa
                </Button>
            </Grid>
        );
    };

    const getRateListTable = () => {
        if (!contextRateList.length || 
            contextRateList.filter((p: SocialMediaRateModel) => p.editAction != EditAction.DELETE).length == 0 ) {

            return (
                <Grid item xs={12}>
                    <TableEmpty 
                        displayText="Nenhuma taxa cadastrada" 
                        cssProperties={{ minHeight: '150px'}} />
                </Grid>
            );

        } else {

            return (
                <Grid item xs={12}>
                    <TableCommon
                        tableData={contextRateList.filter((e) => e.editAction != EditAction.DELETE)}
                        tableColumns={rateTableColumns}
                        tableActions={rateTableActions}
                        extraOptions={rateTableOptions}
                        emptyDataSourceMessage='Nenhuma taxa cadastrada' />
                </Grid>
            );
        }
    };

    const getRateModalForm = () => {
        return (
            <Grid container spacing={3}>
                { getModalRateValue() }
                { getModalStartOfPeriod() }
            </Grid>
        );
    };

    const getModalRateValue = () => {
        return (
            <Grid item xs={12} sm={6}>
                <TextField 
                    id='client-rate-value'
                    fullWidth
                    label="Valor da taxa"
                    variant="outlined"
                    type="number" 
                    value={modalRateValue ? modalRateValue : (modalRateValue === 0 ? 0 : '')}
                    onChange={handleModalRateValueChange}
                    error={modalRateValueError && (modalRateValue == null || isNaN(modalRateValue) || modalRateValue < 0)}
                    helperText={(modalRateValueError && (modalRateValue == null ||isNaN(modalRateValue) ||  modalRateValue < 0)) &&
                        'Campo obrigatório'} />
            </Grid>
        );
    };

    const getModalStartOfPeriod = () => {
        return (
            <Grid item xs={12} sm={6}>
                <MuiPickersUtilsProvider locale={ptBR} utils={DateFnsUtils}>
                    <DatePicker
                        fullWidth
                        autoOk
                        allowKeyboardControl
                        id="rate-start-of-period"
                        variant="inline"
                        value={modalRateStartOfPeriod}
                        inputVariant="outlined"
                        label="Data de início de vigência"
                        onChange={(date: MaterialUiPickersDate) => setModalRateStartOfPeriod(date)}
                        format="dd/MM/yyyy"
                        onBlur={() => setModalRateStartOfPeriodFocused(false)}
                        inputRef={input => modalRateStartOfPeriodFocused && input && input.focus()}
                        invalidDateMessage='Formato de data inválido'
                        open={modalRateStartOfPeriodFocused}
                        disablePast
                        onClose={() => setModalRateStartOfPeriodFocused(false)}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton onClick={() => setModalRateStartOfPeriodFocused(true)}>
                                        <Icon>date_range</Icon>
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                        error={modalRateStartOfPeriodError && !modalRateStartOfPeriod}
                        helperText={modalRateStartOfPeriodError && !modalRateStartOfPeriod && 'Campo obrigatório'} />
                </MuiPickersUtilsProvider>
            </Grid>
        );
    };

    return (
        <>
            <Grid container direction="column" spacing={1}>               
                <Grid item xs={12}>
                    <Typography variant="subtitle1" >{itemTitle}</Typography>
                </Grid>
                { getAddNewButton() }
                { getRateListTable() }
            </Grid>

            <BasicConfirmDialog
                title="Adicionar taxa de conversão"
                description={'Para adicionar uma nova taxa, preencha os campos de valor e data de início de vigência.'}
                handleFirstButton={handleCloseAndClearModal}
                handleSecondButton={handleRateModalSave}
                textFirstButton="Descartar"
                textSecondButton="Salvar taxa"
                open={addNewRateDialogOpen}
                children={getRateModalForm()}
                cssProperties={{ 
                    minWidth: '700px',
                    maxWidth: '700px',
                    padding: '1rem',
                    colorFirstButton: theme.palette.primary.main,
                    justifyButtons: 'flex-end',
                    titleVariant: 'h4',
                    descriptionVariant: 'body1'
                }} />

            <BasicConfirmDialog
                title="Excluir taxa de conversão?"
                description="Tem certeza que deseja excluir esta taxa de conversão?"
                handleFirstButton={() => handleDeleteCurrentRate()}
                handleSecondButton={() => setDeleteRateDialogOpen(false)}
                textFirstButton="Excluir"
                textSecondButton="Voltar"
                reverseButtons={true}
                open={deleteRateDialogOpen} />
        </>
    );
}